/**
 * CAOSLAB.DEV — FIREBASE BACKEND SERVERLESS SERVICE
 * Integrates Firebase Auth, Cloud Firestore & Cloud Storage
 */

import { initializeApp, getApps, getApp } from "firebase/app";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from "firebase/auth";
import { 
  initializeFirestore,
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp, 
  onSnapshot 
} from "firebase/firestore";
import { 
  getStorage, 
  ref, 
  uploadBytesResumable, 
  getDownloadURL 
} from "firebase/storage";

// Firebase Configuration from Environment or Project Credentials
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyB7GLbQZZpSG7U17sNPefr-FOSFEhvURrA",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "blog-caos.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "blog-caos",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "blog-caos.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "87959200253",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:87959200253:web:1e4bd4daf0d2839b753994"
};

// Initialize Firebase Singleton
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
export const auth = getAuth(app);
export const db = !getApps().length
  ? initializeFirestore(app, { experimentalAutoDetectLongPolling: true })
  : getFirestore(app);
export const storage = getStorage(app);

/* ==========================================================================
   AUTHENTICATION UTILITIES
   ========================================================================== */

/**
 * Sign in admin or editor with Email and Password
 */
export async function loginUser(email, password) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error) {
    console.error("Firebase Auth Error:", error);
    return { user: null, error: error.message || "Error al iniciar sesión" };
  }
}

/**
 * Sign out current user
 */
export async function logoutUser() {
  try {
    await signOut(auth);
    return { success: true, error: null };
  } catch (error) {
    console.error("Firebase SignOut Error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Observe authentication state changes
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

/**
 * Get currently authenticated user
 */
export function getCurrentUser() {
  return auth.currentUser;
}


/* ==========================================================================
   CONTACTS & LEADS MANAGEMENT (Firestore)
   ========================================================================== */

/**
 * Save new lead from contact form
 */
export async function createContactLead(leadData) {
  try {
    const contactsRef = collection(db, "contacts");
    const docRef = await addDoc(contactsRef, {
      name: leadData.name || "",
      email: leadData.email || "",
      phone: leadData.phone || "No especificado",
      service: leadData.service || "Desarrollo Web",
      message: leadData.message || "",
      status: "unread", // 'unread' | 'read' | 'contacted'
      createdAt: serverTimestamp()
    });
    return { id: docRef.id, success: true, error: null };
  } catch (error) {
    console.error("Error creating contact lead:", error);
    return { id: null, success: false, error: error.message };
  }
}

/**
 * Get all contact leads (Admin only)
 */
export async function getContactLeads() {
  try {
    const contactsRef = collection(db, "contacts");
    const q = query(contactsRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    const leads = [];
    snapshot.forEach(docSnap => {
      leads.push({ id: docSnap.id, ...docSnap.data() });
    });
    return { leads, error: null };
  } catch (error) {
    console.error("Error fetching contact leads:", error);
    return { leads: [], error: error.message };
  }
}

/**
 * Realtime subscription to contact leads (Admin)
 */
export function subscribeContactLeads(callback, errorCallback) {
  const contactsRef = collection(db, "contacts");
  const q = query(contactsRef, orderBy("createdAt", "desc"));
  return onSnapshot(q, (snapshot) => {
    const leads = [];
    snapshot.forEach(docSnap => {
      leads.push({ id: docSnap.id, ...docSnap.data() });
    });
    callback(leads);
  }, (err) => {
    console.error("Leads subscription error:", err);
    if (errorCallback) errorCallback(err);
  });
}

/**
 * Update lead status
 */
export async function updateLeadStatus(leadId, status) {
  try {
    const leadRef = doc(db, "contacts", leadId);
    await updateDoc(leadRef, { status });
    return { success: true, error: null };
  } catch (error) {
    console.error("Error updating lead status:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete a contact lead
 */
export async function deleteLead(leadId) {
  try {
    const leadRef = doc(db, "contacts", leadId);
    await deleteDoc(leadRef);
    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting lead:", error);
    return { success: false, error: error.message };
  }
}


/* ==========================================================================
   BLOG POSTS MANAGEMENT (Firestore)
   ========================================================================== */

/**
 * Fetch all published posts for public view (with safe network timeout)
 */
export async function getPublishedPosts() {
  try {
    const fetchTask = (async () => {
      const postsRef = collection(db, "posts");
      const q = query(postsRef, where("status", "==", "published"));
      const snapshot = await getDocs(q);
      const posts = [];
      snapshot.forEach(docSnap => {
        posts.push({ id: docSnap.id, ...docSnap.data() });
      });
      posts.sort((a, b) => {
        const timeA = a.publishedAt?.toMillis ? a.publishedAt.toMillis() : (a.createdAt?.toMillis ? a.createdAt.toMillis() : 0);
        const timeB = b.publishedAt?.toMillis ? b.publishedAt.toMillis() : (b.createdAt?.toMillis ? b.createdAt.toMillis() : 0);
        return timeB - timeA;
      });
      return { posts, error: null };
    })();

    const timeoutTask = new Promise(resolve => 
      setTimeout(() => resolve({ posts: [], error: "network_timeout" }), 3500)
    );

    return await Promise.race([fetchTask, timeoutTask]);
  } catch (error) {
    return { posts: [], error: error.message };
  }
}

/**
 * Fetch all posts including drafts (Admin)
 */
export async function getAllPosts() {
  try {
    const postsRef = collection(db, "posts");
    const snapshot = await getDocs(postsRef);
    const posts = [];
    snapshot.forEach(docSnap => {
      posts.push({ id: docSnap.id, ...docSnap.data() });
    });
    posts.sort((a, b) => {
      const timeA = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : (a.createdAt?.toMillis ? a.createdAt.toMillis() : 0);
      const timeB = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : (b.createdAt?.toMillis ? b.createdAt.toMillis() : 0);
      return timeB - timeA;
    });
    return { posts, error: null };
  } catch (error) {
    console.error("Error fetching all posts:", error);
    return { posts: [], error: error.message };
  }
}

/**
 * Realtime subscription to all posts (Admin)
 */
export function subscribeAllPosts(callback, errorCallback) {
  const postsRef = collection(db, "posts");
  return onSnapshot(postsRef, (snapshot) => {
    const posts = [];
    snapshot.forEach(docSnap => {
      posts.push({ id: docSnap.id, ...docSnap.data() });
    });
    posts.sort((a, b) => {
      const timeA = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : (a.createdAt?.toMillis ? a.createdAt.toMillis() : 0);
      const timeB = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : (b.createdAt?.toMillis ? b.createdAt.toMillis() : 0);
      return timeB - timeA;
    });
    callback(posts);
  }, (err) => {
    console.error("Posts subscription error:", err);
    if (errorCallback) errorCallback(err);
  });
}

/**
 * Fetch a single post by slug or ID
 */
export async function getPostBySlugOrId(slugOrId) {
  try {
    // 1. Try document ID first
    const docRef = doc(db, "posts", slugOrId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { post: { id: docSnap.id, ...docSnap.data() }, error: null };
    }

    // 2. Query by slug
    const postsRef = collection(db, "posts");
    const q = query(postsRef, where("slug", "==", slugOrId));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const firstDoc = snapshot.docs[0];
      return { post: { id: firstDoc.id, ...firstDoc.data() }, error: null };
    }

    return { post: null, error: "Artículo no encontrado" };
  } catch (error) {
    console.error("Error fetching post by slug or ID:", error);
    return { post: null, error: error.message };
  }
}

/**
 * Create or update a blog post
 */
export async function savePost(postData, postId = null) {
  try {
    const user = auth.currentUser;
    const cleanSlug = (postData.slug || postData.title || "post")
      .toLowerCase()
      .trim()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // remove accents
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    const payload = {
      title: postData.title || "Sin título",
      slug: cleanSlug,
      content: postData.content || "",
      excerpt: postData.excerpt || "",
      coverImageUrl: postData.coverImageUrl || "",
      status: postData.status || "draft", // 'draft' | 'published'
      category: postData.category || "Desarrollo Web",
      categoryKey: postData.categoryKey || "web",
      authorId: user ? user.uid : "caoslab-admin",
      authorName: postData.authorName || (user ? (user.displayName || user.email) : "Equipo CaosLab"),
      authorRole: postData.authorRole || "Arquitectura & Desarrollo",
      readTime: postData.readTime || "5 min de lectura",
      updatedAt: serverTimestamp()
    };

    if (payload.status === "published" && !postData.publishedAt) {
      payload.publishedAt = serverTimestamp();
    } else if (postData.publishedAt) {
      payload.publishedAt = postData.publishedAt;
    }

    if (postId) {
      const postRef = doc(db, "posts", postId);
      await updateDoc(postRef, payload);
      return { id: postId, success: true, error: null };
    } else {
      payload.createdAt = serverTimestamp();
      const postsRef = collection(db, "posts");
      const docRef = await addDoc(postsRef, payload);
      return { id: docRef.id, success: true, error: null };
    }
  } catch (error) {
    console.error("Error saving post:", error);
    return { id: null, success: false, error: error.message };
  }
}

/**
 * Delete a blog post
 */
export async function deletePost(postId) {
  try {
    const postRef = doc(db, "posts", postId);
    await deleteDoc(postRef);
    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting post:", error);
    return { success: false, error: error.message };
  }
}


/* ==========================================================================
   IMAGE PROCESSING & STORAGE UTILITIES (Direct Base64 / Storage)
   ========================================================================== */

/**
 * Optimizes and converts an image file to a compressed Base64 Data URI
 * Eliminates requirement for Cloud Storage by saving directly in Firestore
 */
export function processAndCompressImage(file, maxWidth = 1200, maxHeight = 800, quality = 0.8) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No se proporcionó ningún archivo."));

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      return reject(new Error("Formato inválido. Selecciona una imagen JPG, PNG o WEBP."));
    }

    // For SVGs, read directly as data URL
    if (file.type === "image/svg+xml") {
      const reader = new FileReader();
      reader.onload = (e) => resolve({ downloadUrl: e.target.result });
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Scale down while preserving aspect ratio
        if (width > maxWidth || height > maxHeight) {
          if (width / height > maxWidth / maxHeight) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to compact WebP or JPEG
        let dataUrl = canvas.toDataURL("image/webp", quality);
        // Fallback to jpeg if browser doesn't support webp canvas export
        if (!dataUrl.startsWith("data:image/webp")) {
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }

        resolve({ downloadUrl: dataUrl, width, height });
      };
      img.onerror = () => reject(new Error("No se pudo procesar la imagen seleccionada."));
      img.src = e.target.result;
    };
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
}

/**
 * Upload cover or content image
 * Uses client-side compression and direct Base64 embedding with optional Storage fallback
 */
export async function uploadBlogImage(file, folder = "covers", onProgress = null) {
  if (onProgress) onProgress(30);
  try {
    const result = await processAndCompressImage(file);
    if (onProgress) onProgress(100);
    return result;
  } catch (err) {
    console.error("Image processing error:", err);
    throw err;
  }
}
